import React from 'react';
import { useParams } from 'react-router-dom';
import { SideBar } from '../../sidebar';
import i18n from '../../../../i18n';
import HeaderBanner from '../../../common/HeaderBanner';
import { formatDate } from '../../../../utils/common';
import { StatusNewBadge } from '../../../common/StatusBadge';
import { AUCTION_REQUEST_STATUS, AUCTION_REQUEST_TYPE } from '../../../../constants/status';
import { ArrowLeft, Expand, Shrink, ImageOff, Star } from 'lucide-react';
import '../../../../styles/components/create-group.scss';
import { getAuctionRequestDetail, approveAuctionRequest } from './services/auctionRequestService';
import { getAllCategories } from '../../../../models/categories';

const APPROVE_FORM_DEFAULTS = {
  category: '',
  has_auto_bidding: false,
  is_vat_included: false,
  is_service_charge_included: false,
  is_service_charge_vat_included: false,
  vat: '',
  // Matches CreateAuctionGeneral.jsx's payment_type select — a plain enum
  // column on Auction, not a lookup list.
  payment_type: '',
  payment_amount: '',

  // --- ported from the old manual-create flow (CreateAuctionGeneral.jsx),
  // missing from the YMS-driven approve flow entirely until now ---
  description: '',
  description_ar: '',
  terms: '',
  terms_arabic: '',
  visit_amount: '',
  contract_number: '',
  mask: '',
  latitude: '',
  longitude: '',
  is_featured: true,
  is_zakath: false,
  auto_approval: true,
  is_extra_time: false,
  extra_time: '',
  is_direct_sale: false,
  is_vehicle: false,
};

class AuctionRequestViewClass extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      request: null,
      isloading: true,
      notFound: false,
      hideSidebar: false,

      // --- inline approve panel, same pattern as GroupRequestView: no
      // separate route/page, just a toggleable section on this page ---
      showApproveForm: false,
      approving: false,
      approveError: null,
      approveForm: { ...APPROVE_FORM_DEFAULTS },
      categories: [],
    };
  }

  componentDidMount() {
    window.scrollTo(0, 0);
    this.fetchDetail();
    this.loadCategories();
  }

  async loadCategories() {
    try {
      const response = await getAllCategories();
      this.setState({ categories: response.data || [] });
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  }

  async fetchDetail() {
    const routeParams = this.props.params || {};
    const ymsAuctionId = routeParams.yms_auction_id ?? Object.values(routeParams)[0];

    if (!ymsAuctionId) {
      console.error('AuctionRequestView: no route param found. this.props.params =', routeParams);
      this.setState({ isloading: false, notFound: true });
      return;
    }

    this.setState({ isloading: true });
    try {
      const response = await getAuctionRequestDetail(ymsAuctionId);
      if (response?.success) {
        this.setState({ request: response.data, notFound: false });
      } else {
        this.setState({ notFound: true });
      }
    } catch (error) {
      console.error('Auction request detail fetch failed:', error);
      this.setState({ notFound: true });
    } finally {
      this.setState({ isloading: false });
    }
  }

  renderField(label, value) {
    return (
      <div className="form-field">
        <label>{label}</label>
        <div className="view-field-value">{value ?? value === 0 ? value : '-'}</div>
      </div>
    );
  }

  renderImages(images) {
    if (!images || !images.length) {
      return (
        <div className="attachment-empty">
          <ImageOff size={16} />
          <span>{i18n.t('no_images') || 'No images'}</span>
        </div>
      );
    }

    return (
      <div className="request-image-gallery" style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
        {[...images]
          .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
          .map((img) => (
            <div key={img.id} style={{ position: 'relative', width: '140px' }}>
              <img
                src={img.url || img.image_url}
                alt=""
                style={{ width: '140px', height: '100px', objectFit: 'cover', borderRadius: '6px', border: '1px solid #e5e7eb' }}
              />
              {img.is_cover && (
                <span
                  style={{
                    position: 'absolute',
                    top: '4px',
                    left: '4px',
                    background: 'rgba(0,0,0,0.6)',
                    color: '#fff',
                    borderRadius: '4px',
                    padding: '2px 6px',
                    fontSize: '11px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                  }}
                >
                  <Star size={11} fill="#fbbf24" color="#fbbf24" />
                  {i18n.t('cover') || 'Cover'}
                </span>
              )}
            </div>
          ))}
      </div>
    );
  }

  renderAttributes(attributes) {
    if (!attributes || !attributes.length) {
      return <div className="attachment-empty">{i18n.t('no_attributes') || 'No attributes'}</div>;
    }

    return (
      <table className="view-attributes-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <th style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid #e5e7eb' }}>
              {i18n.t('attribute') || 'Attribute'}
            </th>
            <th style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid #e5e7eb' }}>
              {i18n.t('value') || 'Value'}
            </th>
          </tr>
        </thead>
        <tbody>
          {attributes.map((attr) => (
            <tr key={attr.id}>
              <td style={{ padding: '6px 8px', borderBottom: '1px solid #f3f4f6' }}>
                {attr.attribute?.name || `#${attr.attribute_id}`}
              </td>
              <td style={{ padding: '6px 8px', borderBottom: '1px solid #f3f4f6' }}>{attr.value}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }

  handleApproveFieldChange = (e) => {
    const { name, value } = e.target;
    this.setState((prev) => ({
      approveForm: { ...prev.approveForm, [name]: value },
    }));
  };

  handleApproveToggle = (field) => {
    this.setState((prev) => ({
      approveForm: { ...prev.approveForm, [field]: !prev.approveForm[field] },
    }));
  };

  handleApproveSubmit = async () => {
    const { request, approveForm } = this.state;

    if (!request?.group_id) {
      this.setState({ approveError: 'Approve the parent group request before approving this auction.' });
      return;
    }

    if (!approveForm.category) {
      this.setState({ approveError: 'Category is required.' });
      return;
    }

    this.setState({ approving: true, approveError: null });

    try {
      const payload = {
        category: approveForm.category,
        has_auto_bidding: approveForm.has_auto_bidding,
        is_vat_included: approveForm.is_vat_included,
        is_service_charge_included: approveForm.is_service_charge_included,
        is_service_charge_vat_included: approveForm.is_service_charge_vat_included,
        vat: approveForm.vat || undefined,
        payment_type: approveForm.payment_type || undefined,
        payment_amount: approveForm.payment_amount || undefined,

        description: approveForm.description || undefined,
        description_ar: approveForm.description_ar || undefined,
        terms: approveForm.terms || undefined,
        terms_arabic: approveForm.terms_arabic || undefined,
        visit_amount: approveForm.visit_amount || undefined,
        contract_number: approveForm.contract_number || undefined,
        mask: approveForm.mask || undefined,
        latitude: approveForm.latitude || undefined,
        longitude: approveForm.longitude || undefined,
        is_featured: approveForm.is_featured,
        is_zakath: approveForm.is_zakath,
        auto_approval: approveForm.auto_approval,
        is_extra_time: approveForm.is_extra_time,
        extra_time: approveForm.is_extra_time ? approveForm.extra_time || 0 : undefined,
        is_direct_sale: approveForm.is_direct_sale,
        is_vehicle: approveForm.is_vehicle,
      };

      const response = await approveAuctionRequest(request.public_id, payload);

      if (response?.success) {
        this.setState({
          showApproveForm: false,
          approveForm: { ...APPROVE_FORM_DEFAULTS },
        });
        // Re-fetch so the badge/status here flips to 'synced' right away.
        this.fetchDetail();
      } else {
        this.setState({ approveError: response?.message || 'Approval failed.' });
      }
    } catch (error) {
      console.error('Auction request approval failed:', error);
      this.setState({
        approveError:
          error?.response?.data?.message || error?.message || 'Approval failed. Check the form and try again.',
      });
    } finally {
      this.setState({ approving: false });
    }
  };

  renderApprovePanel() {
    const { approveForm, approving, approveError, categories } = this.state;

    return (
      <div className="form-section" style={{ border: '1px solid #e5e7eb', borderRadius: '8px', padding: '16px', marginTop: '16px' }}>
        <h2>{i18n.t('approve_auction_request') || 'Approve & Create Auction'}</h2>
        <p style={{ color: '#6b7280', fontSize: '0.85rem' }}>
          {i18n.t('approve_auction_request_hint') ||
            "YMS doesn't send these fields — fill them in to create the live auction."}
        </p>

        {approveError && (
          <div className="attachment-empty" style={{ color: '#ef4444', marginBottom: '12px' }}>
            {approveError}
          </div>
        )}

        <div className="form-row">
          <div className="form-field">
            <label>Category *</label>
            <select name="category" value={approveForm.category} onChange={this.handleApproveFieldChange}>
              <option value="">--Category--</option>
              {categories.length > 0 &&
                categories.map((item) => (
                  <option value={item.id} key={item.id}>
                    {item.category_name}
                  </option>
                ))}
            </select>
          </div>
          <div className="form-field">
            <label>{i18n.t('vat') || 'VAT (%)'}</label>
            <input type="text" name="vat" value={approveForm.vat} onChange={this.handleApproveFieldChange} />
          </div>
        </div>

        <div className="form-row">
          <div className="form-field">
            <label>{i18n.t('payment_type') || 'Payment Type'}</label>
            {/* Matches CreateAuctionGeneral.jsx exactly — fixed enum, not
                a lookup list. */}
            <select name="payment_type" value={approveForm.payment_type} onChange={this.handleApproveFieldChange}>
              <option value="">--Select--</option>
              <option value="Online">{i18n.t('online') || 'Online'}</option>
              <option value="Offline">{i18n.t('offline') || 'Offline'}</option>
              <option value="Both">Supports both Online & Offline</option>
            </select>
          </div>
          <div className="form-field">
            <label>{i18n.t('Enrollment amount') || 'Payment Amount'}</label>
            <input
              type="number"
              name="payment_amount"
              value={approveForm.payment_amount}
              onChange={this.handleApproveFieldChange}
              onWheel={(e) => e.target.blur()}
              step="0.01"
              min="0"
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-field">
            <label>{i18n.t('description') || 'Description'}</label>
            <textarea name="description" value={approveForm.description} onChange={this.handleApproveFieldChange} rows={3}></textarea>
          </div>
          <div className="form-field">
            <label>{i18n.t('description')} ({i18n.t('arabic') || 'Arabic'})</label>
            <textarea name="description_ar" value={approveForm.description_ar} onChange={this.handleApproveFieldChange} dir="rtl" rows={3}></textarea>
          </div>
        </div>

        <div className="form-row">
          <div className="form-field">
            <label>{i18n.t('terms_conditions') || 'Terms & Conditions'}</label>
            <textarea name="terms" value={approveForm.terms} onChange={this.handleApproveFieldChange} rows={3}></textarea>
          </div>
          <div className="form-field">
            <label>{i18n.t('terms_conditions')} ({i18n.t('arabic') || 'Arabic'})</label>
            <textarea name="terms_arabic" value={approveForm.terms_arabic} onChange={this.handleApproveFieldChange} dir="rtl" rows={3}></textarea>
          </div>
        </div>

        <div className="form-row">
          <div className="form-field">
            <label>{i18n.t('visit_amount') || 'Visit Amount'}</label>
            <input type="number" name="visit_amount" value={approveForm.visit_amount} onChange={this.handleApproveFieldChange} onWheel={(e) => e.target.blur()} step="0.01" min="0" />
          </div>
          <div className="form-field">
            <label>{i18n.t('contract_number') || 'Contract Number'}</label>
            <input type="text" name="contract_number" value={approveForm.contract_number} onChange={this.handleApproveFieldChange} />
          </div>
        </div>

        <div className="form-row">
          <div className="form-field">
            <label>Latitude</label>
            <input type="text" name="latitude" value={approveForm.latitude} onChange={this.handleApproveFieldChange} />
          </div>
          <div className="form-field">
            <label>Longitude</label>
            <input type="text" name="longitude" value={approveForm.longitude} onChange={this.handleApproveFieldChange} />
          </div>
        </div>

        <div className="toggle-field">
          <label>Is Featured</label>
          <div
            className={`toggle ${approveForm.is_featured ? 'active' : ''}`}
            onClick={() => this.handleApproveToggle('is_featured')}
          >
            <div className="toggle-slider"></div>
          </div>
        </div>
        <div className="toggle-field">
          <label>Is Zakath</label>
          <div
            className={`toggle ${approveForm.is_zakath ? 'active' : ''}`}
            onClick={() => this.handleApproveToggle('is_zakath')}
          >
            <div className="toggle-slider"></div>
          </div>
        </div>
        <div className="toggle-field">
          <label>Auto Approval</label>
          <div
            className={`toggle ${approveForm.auto_approval ? 'active' : ''}`}
            onClick={() => this.handleApproveToggle('auto_approval')}
          >
            <div className="toggle-slider"></div>
          </div>
        </div>
        <div className="toggle-field">
          <label>{i18n.t('auto_biding') || 'Auto Bidding'}</label>
          <div
            className={`toggle ${approveForm.has_auto_bidding ? 'active' : ''}`}
            onClick={() => this.handleApproveToggle('has_auto_bidding')}
          >
            <div className="toggle-slider"></div>
          </div>
        </div>
        <div className="toggle-field">
          <label>{i18n.t('extra_time') || 'Extra Time'}</label>
          <div
            className={`toggle ${approveForm.is_extra_time ? 'active' : ''}`}
            onClick={() => this.handleApproveToggle('is_extra_time')}
          >
            <div className="toggle-slider"></div>
          </div>
        </div>
        {approveForm.is_extra_time && (
          <div className="form-field" style={{ marginTop: '12px' }}>
            <label>{i18n.t('extra_time')} ({i18n.t('in_minutes') || 'minutes'})</label>
            <input
              type="number"
              name="extra_time"
              value={approveForm.extra_time}
              onChange={this.handleApproveFieldChange}
            />
          </div>
        )}
        <div className="toggle-field">
          <label>{i18n.t('vat_included') || 'VAT Included'}</label>
          <div
            className={`toggle ${approveForm.is_vat_included ? 'active' : ''}`}
            onClick={() => this.handleApproveToggle('is_vat_included')}
          >
            <div className="toggle-slider"></div>
          </div>
        </div>
        <div className="toggle-field">
          <label>{i18n.t('service_charge_included') || 'Service Charge Included'}</label>
          <div
            className={`toggle ${approveForm.is_service_charge_included ? 'active' : ''}`}
            onClick={() => this.handleApproveToggle('is_service_charge_included')}
          >
            <div className="toggle-slider"></div>
          </div>
        </div>
        <div className="toggle-field">
          <label>{i18n.t('service_charge_vat_included') || 'Service Charge VAT Included'}</label>
          <div
            className={`toggle ${approveForm.is_service_charge_vat_included ? 'active' : ''}`}
            onClick={() => this.handleApproveToggle('is_service_charge_vat_included')}
          >
            <div className="toggle-slider"></div>
          </div>
        </div>

        <div className="toggle-field">
          <label>{i18n.t('is_direct_sale') || 'Direct Sale'}</label>
          <div
            className={`toggle ${approveForm.is_direct_sale ? 'active' : ''}`}
            onClick={() => this.handleApproveToggle('is_direct_sale')}
          >
            <div className="toggle-slider"></div>
          </div>
        </div>
        <div className="toggle-field">
          <label>{i18n.t('is_vehicle') || 'Is Vehicle'}</label>
          <div
            className={`toggle ${approveForm.is_vehicle ? 'active' : ''}`}
            onClick={() => this.handleApproveToggle('is_vehicle')}
          >
            <div className="toggle-slider"></div>
          </div>
        </div>

        <div className="form-row" style={{ marginTop: '12px' }}>
          <button
            className="button1 button1-gradient button1-action"
            disabled={approving}
            onClick={this.handleApproveSubmit}
          >
            {approving ? i18n.t('submitting') || 'Submitting...' : i18n.t('submit') || 'Submit & Create Auction'}
          </button>
          <button
            className="button1 button1-outline button1-action"
            style={{ marginLeft: '8px' }}
            disabled={approving}
            onClick={() =>
              this.setState({
                showApproveForm: false,
                approveError: null,
                approveForm: { ...APPROVE_FORM_DEFAULTS },
              })
            }
          >
            {i18n.t('cancel') || 'Cancel'}
          </button>
        </div>
      </div>
    );
  }

  render() {
    const { request, isloading, notFound, hideSidebar, showApproveForm } = this.state;

    return (
      <React.Fragment>
        <HeaderBanner
          title={i18n.t('auction_request_details') || 'Auction Request Details'}
          subtitle={i18n.t('review_auction_request_from_yms') || 'Details submitted by YMS for this auction'}
        />
        <section className="dashboard-section">
          <div className="container">
            <div className="row">
              <div className="col-xl-3 col-lg-12 col-md-12">{!hideSidebar && <SideBar />}</div>
              <div
                className={hideSidebar ? 'col-xl-12 col-lg-12 col-md-12' : 'col-xl-9 col-lg-12 col-md-12'}
              >
                <div className="card">
                  <div className="card-header">
                    <div className="expand-dashboard">
                      <span
                        style={{ fontSize: '1rem', marginRight: '0.5rem', cursor: 'pointer' }}
                        onClick={() => this.setState({ hideSidebar: !hideSidebar })}
                      >
                        {hideSidebar && <Shrink strokeWidth={1} />}
                        {!hideSidebar && <Expand strokeWidth={1} />}
                      </span>
                    </div>
                    <div className="header-action">

                      {request && request.status !== 'synced' && !showApproveForm && (
                        <button
                          className="button1 button1-gradient button1-action"
                          style={{ marginRight: '0.5rem' }}
                          disabled={!request.group_id}
                          title={!request.group_id ? (i18n.t('approve_group_first') || 'Approve the group request first') : undefined}
                          onClick={() => this.setState({ showApproveForm: true })}
                        >
                          {i18n.t('approve') || 'Approve'}
                        </button>
                      )}
                      <button
                        className="button1 button1-gradient button1-back"
                        onClick={() => window.history.back()}
                      >
                        <ArrowLeft size={18} className="icon" />
                        <span>{i18n.t('back')}</span>
                      </button>
                    </div>
                  </div>
                  <div className="card-body">
                    {isloading ? (
                      <div className="attachment-empty">{i18n.t('loading') || 'Loading...'}</div>
                    ) : notFound || !request ? (
                      <div className="attachment-empty">
                        {i18n.t('auction_request_not_found') || 'Auction request not found'}
                      </div>
                    ) : (
                      <div className="create-group-container">
                        {!request.group_id && request.status !== 'synced' && (
                          <div
                            className="form-row"
                            style={{
                              background: '#fffbeb',
                              border: '1px solid #fcd34d',
                              borderRadius: '6px',
                              padding: '10px 14px',
                              color: '#92400e',
                              fontSize: '0.85rem',
                              marginBottom: '12px',
                            }}
                          >
                            {i18n.t('approve_group_before_auction') ||
                              'The group for this auction hasn\'t been approved yet. Approve the group request first, then come back to approve this auction.'}
                          </div>
                        )}

                        <div className="form-section">
                          <h2>
                            {request.title || request.yms_auction_id}{' '}
                            <StatusNewBadge status={AUCTION_REQUEST_STATUS[request.status]} />{' '}
                            <StatusNewBadge status={AUCTION_REQUEST_TYPE[request.request_type]} />
                          </h2>

                          <div className="form-row">
                            {this.renderField(i18n.t('yms_auction_id') || 'YMS Auction ID', request.yms_auction_id)}
                            {this.renderField(i18n.t('yms_group_id') || 'YMS Group ID', request.yms_group_id)}
                            {this.renderField('Title (EN)', request.title)}
                            {this.renderField('Title (AR)', request.title_ar)}
                          </div>

                          <div className="form-row">
                            {this.renderField(i18n.t('start_amount') || 'Start Amount', request.start_amount)}
                            {this.renderField(i18n.t('guarantee_amount') || 'Guarantee Amount', request.guarantee_amount)}
                            {this.renderField(i18n.t('bid_increment') || 'Bid Increment', request.bid_increment)}
                            {this.renderField(
                              i18n.t('increment_numbers') || 'Increment Options',
                              request.increment_numbers?.join(', ')
                            )}
                          </div>

                          <div className="form-row">
                            {this.renderField('Location (EN)', request.location)}
                            {this.renderField('Location (AR)', request.location_ar)}
                            {this.renderField(i18n.t('start_date') || 'Start Date', request.start_date ? formatDate(request.start_date) : '-')}
                            {this.renderField(i18n.t('end_date') || 'End Date', request.end_date ? formatDate(request.end_date) : '-')}
                          </div>

                          <div className="form-row">
                            {this.renderField(
                              i18n.t('reg_start_date') || 'Registration Start',
                              request.reg_start_date ? formatDate(request.reg_start_date) : '-'
                            )}
                            {this.renderField(
                              i18n.t('reg_end_date') || 'Registration End',
                              request.reg_end_date ? formatDate(request.reg_end_date) : '-'
                            )}
                            {this.renderField(i18n.t('submitted_on') || 'Submitted On', request.created_at ? formatDate(request.created_at) : '-')}
                            {this.renderField(i18n.t('last_updated') || 'Last Updated', request.updated_at ? formatDate(request.updated_at) : '-')}
                          </div>

                          {request.reason && (
                            <div className="form-row">
                              {this.renderField(i18n.t('reason') || 'Reason', request.reason)}
                            </div>
                          )}
                        </div>

                        <div className="form-section">
                          <h2>{i18n.t('images') || 'Images'}</h2>
                          {this.renderImages(request.images)}
                        </div>

                        <div className="form-section">
                          <h2>{i18n.t('attributes') || 'Attributes'}</h2>
                          {this.renderAttributes(request.attributes)}
                        </div>

                        {showApproveForm && this.renderApprovePanel()}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </React.Fragment>
    );
  }
}

export function AuctionRequestView(props) {
  const params = useParams();
  return <AuctionRequestViewClass {...props} params={params} />;
}